<script setup lang="ts">
import { computed, defineProps, ref, watch } from 'vue'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import GridPartnerNameColumn from '@/components/data-visualization/grids/GridPartnerNameColumn.vue'
import PartnerGridActions from '@/components/features/partners/PartnerGridActions.vue'
import CurrentStatusPartnerInputChoice from '@/components/filters/CurrentStatusPartnerInputChoice.vue'
import type { Partner } from '@/api-swagger'
import { storeToRefs } from 'pinia'
import { useCampaignStore } from '@/stores/useCampaignStore'
import { getCodeNafWithLibelle } from '@/utils/code-naf'
import AddToCampaignButton from '@/components/common/buttons/AddToCampaignButton.vue'

const campaignStore = useCampaignStore()
const { loadingAddCallSessions } = storeToRefs(campaignStore)
const selectedPartners = ref<Partner[]>([])

const props = defineProps({
  data: {
    type: Array,
    required: false,
  },
  selectable: {
    type: Boolean,
    default: false,
  },
  withFilterCurrentStatus: {
    type: Boolean,
    default: true,
  },
  filterCurrentStatus: {
    type: String,
    default: '',
  },
})

const filterValue = ref('')
const filterCol = ref('currentStatus')

const handleSelectionChanged = (selectedRows: Partner[]) => {
  selectedPartners.value = selectedRows
}
watch(
  () => props.filterCurrentStatus,
  (val) => {
    updateFilter({ value: val })
  },
)

function updateFilter({ value }: { value: string }) {
  filterValue.value = value
}

const partners = computed(() => {
  return (
    props.data?.flatMap((partner: any) => {
      return {
        ...partner,
        _adresse: `${partner.street} ${partner.zip} ${partner.city} `,
        _nafCode: getCodeNafWithLibelle(partner.nafCode) || '',
      }
    }) || []
  )
})
</script>

<template>
  <grid-vue
    :list="partners"
    :rowSelection="
      selectable
        ? { mode: 'multiRow', selectAll: 'filtered' }
        : { mode: null, selectAll: 'filtered' }
    "
    :cols="[
      { field: 'currentStatus', headerName: 'currentStatus', sort: 'asc', hide: true },
      {
        field: 'name',
        headerName: 'Nom',
        cellRenderer: GridPartnerNameColumn,
        cellRendererParams: {
          partner: (params: any) => params.data,
        },
        autoHeight: true,
        flex: 2,
      },
      {
        field: 'siret',
        headerName: 'Siret',
        valueFormatter: (params: any) => (!params.value ? '' : params.value),
        cellDataType: 'text',
        flex: 0.65,
      },
      {
        field: '_nafCode',
        headerName: 'Code NAF',
        autoHeight: true,
      },
      {
        field: 'xCa',
        headerName: 'CA',
        autoHeight: true,
        flex: 0.5,
      },
      {
        field: 'company_workforce',
        headerName: 'Nombre employés',
        autoHeight: true,
        flex: 0.65,
      },
      {
        field: 'x_credit_status',
        headerName: 'Status (credit safe)',
        autoHeight: true,
      },
      {
        field: '_adresse',
        headerName: 'Adresse',
        autoHeight: true,
        flex: 1.5,
      },
      {
        field: 'action',
        headerName: 'Actions',
        resizable: false,
        filter: false,
        minWidth: 250,
        maxWidth: 250,
        cellRenderer: PartnerGridActions,
        cellRendererParams: {
          tooltipPosition: 'left',
        },
        sortable: false,
        cellClass: 'border-0 grid-action',
      },
    ]"
    :externalFilterColumn="filterCol"
    :externalFilterValue="filterValue"
    :onUpdateFilter="updateFilter"
    @onSelectionChanged="handleSelectionChanged"
  >
    <template v-slot:filter>
      <current-status-partner-input-choice
        @update:filterValue="updateFilter"
        :filter-value="filterCurrentStatus"
        :class="{ 'hidden ': !withFilterCurrentStatus }"
      />
    </template>
    <template v-slot:button>
      <div v-if="selectedPartners.length > 0">
        <AddToCampaignButton
          :selectedPartners="selectedPartners"
          :loader="loadingAddCallSessions"
          :viewText="true"
        />
      </div>
    </template>
  </grid-vue>
</template>
